import React, { CSSProperties } from 'react'
import { getCdnUrl } from '../../services/environment'
import { getAssetPath } from '../../utils/assetPath'

const containerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  marginRight: '8px',
  flexShrink: 0
}

const validityIndicatorStyle: CSSProperties = {
  width: '5px',
  height: '5px',
  borderRadius: '50%',
  marginRight: '5px',
  flexShrink: 0
}

const iconButtonStyle: CSSProperties = {
  border: 'none',
  background: 'transparent',
  padding: 0,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center'
}

const fileIconStyle: CSSProperties = {
  width: '19.8px',
  height: '26px'
}

interface FileIconProps {
  fileName: string
  fileType: 'FOLDER' | 'DOCUMENT' | 'MULTIPLE'
  isValid: boolean
  isObsolete: boolean
  onIconClick?: () => void
  filesCount?: number // For multiple documents display
  showValidityIndicator?: boolean // Controls whether to show the colored dot
  inWorkflow?: boolean // Whether the file is part of an active workflow
}

const FileIcon: React.FC<FileIconProps> = ({
  fileName,
  fileType,
  isValid,
  isObsolete,
  onIconClick,
  filesCount,
  showValidityIndicator = true,
  inWorkflow = false
}) => {
  // Determine if CR badge should be shown (matching Angular logic)
  const hasCrBadge = () => {
    return inWorkflow
  }

  // Move utility functions here or import them
  const getFileIconPath = (name: string, type: string) => {
    if (type === 'FOLDER') {
      return getAssetPath('assets/images/medidata_folder_icon.svg')
    }

    if (type === 'MULTIPLE') {
      return getAssetPath('assets/images/multiple_docs' + (hasCrBadge() ? '_cr' : '') + '.svg')
    }

    // For documents, use the extension-specific icons
    // Add null safety for fileName
    if (!name || typeof name !== 'string') {
      return getAssetPath(`assets/images/exts/${hasCrBadge() ? 'cr/' : ''}default.png`)
    }
    const extension = name.split('.').pop()?.toLowerCase() ?? ''
    const crPath = hasCrBadge() ? 'cr/' : ''

    switch (extension) {
      case 'docx':
        return getAssetPath(`assets/images/exts/${crPath}docx.png`)
      case 'doc':
        return getAssetPath(`assets/images/exts/${crPath}doc.png`)
      case 'pdf':
        return getAssetPath(`assets/images/exts/${crPath}pdf.png`)
      case 'xls':
        return getAssetPath(`assets/images/exts/${crPath}xls.png`)
      case 'xlsx':
        return getAssetPath(`assets/images/exts/${crPath}xlsx.png`)
      default:
        return getAssetPath(`assets/images/exts/${crPath}default.png`)
    }
  }

  const getValidityColor = () => {
    if (isObsolete) return '#ff6b6b' // obsolete (red)
    if (!isValid) return '#dc3545' // invalid (red)
    return '#28a745' // valid (green)
  }

  const getAltText = () => {
    if (fileType === 'FOLDER') return 'Folder Icon'
    if (fileType === 'MULTIPLE') return 'Multiple Documents'
    return 'Document Icon'
  }

  const getAriaLabel = () => {
    if (fileType === 'FOLDER') return `Open folder ${fileName}`
    if (fileType === 'MULTIPLE') return `Open multiple documents (${filesCount} files)`
    return `Open file ${fileName}`
  }

  const iconPath = getFileIconPath(fileName, fileType)
  const validityColor = getValidityColor()

  return (
    <div style={containerStyle}>
      {showValidityIndicator && (
        <span
          style={{
            ...validityIndicatorStyle,
            backgroundColor: fileType === 'FOLDER' ? 'transparent' : validityColor
          }}
        />
      )}
      <button style={iconButtonStyle} onClick={onIconClick} aria-label={getAriaLabel()}>
        <img src={`${iconPath}`} alt={getAltText()} style={fileIconStyle} />
      </button>
    </div>
  )
}

export default FileIcon
